import { Button } from '@konova/ui/button';
import { Modal, ModalProps } from '@konova/ui/modal';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type ConfirmationDialogProps = ModalProps & {
  /**
   * A callback function that is invoked when a confirmation action is triggered.
   * This function is optional and, if provided, executes without receiving any arguments.
   */
  onConfirm?: () => void;

  /**
   * Callback function that is invoked when a cancel action is triggered.
   * This function does not accept any arguments and does not return a value.
   */
  onCancel?: () => void;

  /**
   * An optional description or additional content that can be rendered within the component.
   * This property accepts a ReactNode, which can include elements, strings, fragments, and more.
   */
  description?: ReactNode;
};

/**
 * ConfirmationDialog is a functional component that renders a modal dialog
 * to confirm or cancel an action. It includes a customizable description
 * and footer with confirm and cancel buttons.
 */
export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  onConfirm,
  onCancel,
  description,
  ...props
}) => {
  const [t] = useTranslation();

  return (
    <Modal
      {...props}
      onClose={onCancel}
      footer={(
        <>
          <Button variant="text" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm}>{t('common.proceed')}</Button>
        </>
      )}
    >
      {description}
    </Modal>
  );
};
